import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Zap, Clock, MapPin, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import CalmEmptyState from "../components/learner/CalmEmptyState";

const API = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (pence) =>
  pence == null ? "" : `£${(pence / 100).toFixed(0)}`;

const formatHoursUntil = (h) => {
  if (h == null) return "";
  if (h < 1) return "starts in <1h";
  if (h < 24) return `in ${Math.round(h)}h`;
  const days = Math.floor(h / 24);
  const rem = Math.round(h - days * 24);
  return rem > 0 ? `in ${days}d ${rem}h` : `in ${days}d`;
};

/**
 * LearnerLastMinute — Phase 9 pupil-facing gap-fill marketplace.
 *
 * Lists open gap_slots within the next 72h that the pupil is allowed
 * to see (existing relationship OR instructor accepting new pupils).
 *
 * Privacy: every field rendered comes from the pre-stripped backend
 * `_public_gap_view()` projection. NO original-pupil names or
 * internal money/ops fields ever reach the DOM.
 */
export default function LearnerLastMinute() {
  const nav = useNavigate();
  const [gaps, setGaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/gaps/pupil-feed`, { withCredentials: true });
      setGaps(r.data?.gaps || []);
    } catch {
      setGaps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const claim = async (g) => {
    if (!window.confirm(`Reserve £${formatPrice(g.estimated_price_pence).replace("£", "")} slot with ${g.instructor_name} on ${g.date} at ${g.start_time}? Your instructor has 12 hours to confirm.`)) return;
    setBusyId(g.id);
    try {
      await axios.post(`${API}/api/gaps/${g.id}/claim`, {}, { withCredentials: true });
      toast.success("Request sent. You'll be notified when your instructor responds.");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't reserve slot. It may already be taken.";
      toast.error(typeof msg === "string" ? msg : "Couldn't reserve slot.");
      load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24" data-testid="learner-last-minute-page">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button
            data-testid="last-minute-back-btn"
            onClick={() => nav("/learn")}
            className="text-slate-400 hover:text-slate-600"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Zap className="w-5 h-5 text-amber-500" />
          <p className="font-extrabold text-slate-900 dark:text-white text-sm flex-1">Last-minute slots</p>
          <span className="text-[10px] text-amber-600 font-bold">72h</span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-3">
        {loading && (
          <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}

        {!loading && gaps && gaps.length === 0 && (
          <CalmEmptyState
            testid="last-minute-empty"
            tone="nothing-yet"
            title="No last-minute slots right now"
            body="Slots within the next 72 hours will appear here when your instructors have an opening."
          />
        )}

        {!loading && gaps && gaps.length > 0 && (
          <p className="text-[11px] text-slate-500 px-1">
            Reserved slots are <strong>pay-later</strong>. Your instructor has 12 hours to accept.
          </p>
        )}

        {!loading && gaps && gaps.map((g) => (
          <div
            key={g.id}
            data-testid={`last-minute-card-${g.id}`}
            className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
          >
            <div className="flex items-start gap-3">
              {g.instructor_avatar ? (
                <img src={g.instructor_avatar} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-amber-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">
                  {g.instructor_name}
                </p>
                <p className="text-[12px] text-slate-600 dark:text-slate-400 mt-0.5">
                  {g.date} · {g.start_time}–{g.end_time} · {g.duration_minutes}m
                </p>
                <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {formatHoursUntil(g.hours_until_start)}
                  </span>
                  {g.postcode_area && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {g.postcode_area}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                {g.estimated_price_pence != null && (
                  <p className="text-base font-extrabold text-slate-900 dark:text-white">
                    {formatPrice(g.estimated_price_pence)}
                  </p>
                )}
                <p className="text-[10px] text-slate-400">est.</p>
              </div>
            </div>
            <button
              data-testid={`last-minute-claim-${g.id}`}
              onClick={() => claim(g)}
              disabled={busyId === g.id}
              className="mt-3 w-full h-10 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busyId === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>Reserve & pay later <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        ))}
      </main>
    </div>
  );
}
